import { useCallback, useContext, useRef, useState } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, Search } from '@vtex/shoreline'
import debounce from 'lodash/debounce'

import { Button } from '~/components/Buttons'
import { FeedbackContext } from '~/components/feedback'
import Form from '~/components/unform/Form'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import { useSubpublishers } from '../../hooks/useSubpublishers'
import {
  addBulkCampaignSubpublishersService,
  listCampaignSubpublishersKey
} from '../../services/campaign/subpublisher'
import { listSubpublisherApprovalStatusKey } from '../../services/campaign/subpublisher-approval-status'
import SubpublishersRadioList from '../SubpublishersRadioList'
import { SUBPUBLISHER_LIST_REFETCH_DELAY_MS } from '../ToggleSubpublisher'

type CampaignAddSubpublishersFormProps = {
  publisherId: PublisherId
  selectedPublishers: PublisherId[]
  onClose?: () => void
  campaignId: CampaignId
  hasAvaliableSubpublishers: boolean
  requireApproval?: boolean
  /** Sub-publishers da rede sem formato compatível (ficam fora da lista). */
  ineligibleCount?: number
}

const CampaignAddSubpublishersForm: React.FC<
  CampaignAddSubpublishersFormProps
> = ({
  publisherId,
  selectedPublishers,
  onClose,
  campaignId,
  hasAvaliableSubpublishers,
  requireApproval,
  ineligibleCount = 0
}) => {
  const { t, tToasts } = useAPPTranslation()
  const { toast } = useContext(FeedbackContext)

  const [searchValue, setSearchValue] = useState('')
  const [name, setName] = useState('')

  const handleDebounceSearch = useRef(debounce(setName, 500)).current

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.target.value)
      handleDebounceSearch(e.target.value)
    },
    [handleDebounceSearch]
  )

  const handleSearchClear = useCallback(() => {
    setSearchValue('')
    setName('')
  }, [])

  const { data, isPending: isSubpublisherPending } = useSubpublishers({
    publisher: publisherId,
    name,
    campaignId,
    matchedOnly: requireApproval
  })

  /**
   * Handle submit
   */
  const queryClient = useQueryClient()

  const queryKey = [listCampaignSubpublishersKey, campaignId]

  const { mutate, isPending } = useMutation({
    mutationFn: addBulkCampaignSubpublishersService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey
      })

      // Ligar um sub-publisher numa campanha já aprovada cria o status `waiting`
      // de forma assíncrona (fila + Lambda); o badge só aparece após este refetch.
      if (requireApproval) {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: [listSubpublisherApprovalStatusKey, campaignId]
          })
        }, SUBPUBLISHER_LIST_REFETCH_DELAY_MS)
      }

      tToasts({
        prefix: 'campaign.subpublishers.add.success',
        toastFunction: toast.success
      })
      onClose?.()
    },
    onError: () => {
      tToasts({
        prefix: 'campaign.subpublishers.add.error',
        toastFunction: toast.critical
      })
    }
  })

  const handleSubmit = useCallback(
    (formData: SubpublisherScope['subpublishers']) => {
      const subpublishers = formData?.subpublisher_ids.filtered

      const payload: AddBulkCampaignSubpublishersResquest = {
        id: campaignId,
        body: {
          subpublisher_ids: subpublishers
        }
      }

      mutate(payload)
    },
    [campaignId, mutate]
  )

  return (
    <section className="p-3">
      <Search
        value={searchValue}
        onChange={handleSearchChange}
        onClear={handleSearchClear}
        messages={{
          placeholder: t('rm:campaign.subpublishers.search.placeholder')
        }}
        className="mb-3"
      />

      <Form onSubmit={handleSubmit}>
        <SubpublishersRadioList
          label={t('rm:campaign.subpublishers.add.label')}
          selectedPublishers={selectedPublishers}
          data={data}
          isPending={isSubpublisherPending}
          hasSearchTerm={!!name}
        />

        {requireApproval && ineligibleCount > 0 && (
          <Alert
            variant="warning"
            style={{ marginBottom: '1rem' }}
            data-testid="ineligibleSubpublishersNotice"
          >
            {t('rm:campaign.subpublishers.add.ineligibleNotice', {
              count: ineligibleCount
            })}
          </Alert>
        )}

        <footer className="d-flex justify-content-between">
          <Button
            text={t('c:actions.cancel')}
            template="transparent"
            size="small"
            onClick={onClose}
            disabled={isPending}
          />
          <Button
            type="submit"
            text={t('c:actions.add')}
            size="small"
            template="primary"
            disabled={isPending || !hasAvaliableSubpublishers}
            loading={isPending}
          />
        </footer>
      </Form>
    </section>
  )
}

export default CampaignAddSubpublishersForm
